import styled from "@emotion/styled";
import { useDispatch, useSelector } from "react-redux";
import Table from "../ui/table/TableController";
import {
  companiesSelector,
  currentSelector,
  updatePayment,
  updatePaymentState,
} from "../../redux/slices/companiesSlice";
import { uiSelector } from "../../redux/slices/uiSlice";
import Loading from "./tableState/Loading";
import NoSearch from "./tableState/NoSearch";
import NoItems from "./tableState/NoItems";

const POS_SECTION = {
  background: "#eef4fd",
  color: "#346fcf",
  dividerColor: "#88aef5",
};

const HOTEL_SECTION = {
  background: "#f4effb",
  color: "#7b68b3",
  dividerColor: "#88aef5",
};

const PAYMENT_FIELD_GROUPS = [
  { base: "pms_id", shortLabel: "HA" },
  { base: "reservation_id", shortLabel: "Res" },
  { base: "pms_account_id", shortLabel: "PMS" },
];

const getIndexedFieldName = (baseName, index) =>
  index === 0 ? baseName : `${baseName}${index}`;

const TablesWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
`;

const HotelTableWrapper = styled.div`
  flex: 1 1 360px;
  min-height: 320px;
  display: flex;
`;

const getHotelPaymentGroups = (payments, currentId) => {
  if (!Array.isArray(payments)) return [];

  if (payments.length && !payments.some((group) => Array.isArray(group?.payments))) {
    const hotelId = String(currentId || payments[0]?.company_id || "").split("-")[0];
    return hotelId ? [{ hotelId, payments }] : [];
  }

  return payments
    .map((group) => ({
      hotelId: String(group?.hotel_id || "").trim(),
      payments: Array.isArray(group?.payments) ? group.payments : [],
    }))
    .filter((group) => group.hotelId && group.payments.length > 0);
};

const getSubHotelIds = (payments) => [
  ...new Set(
    payments
      .map((payment) => String(payment?.hotel_id || "").trim())
      .filter(Boolean)
  ),
];

const getCrossPostingColumns = (payments, parentHotelId) => {
  const hotelIds = getSubHotelIds(payments);
  const columns = hotelIds.length
    ? hotelIds.map((hotelId, index) => ({ hotelId, index }))
    : [
        ...new Set(
          payments.flatMap((payment) =>
            Object.keys(payment || {})
              .filter((key) => /^pms_account_id\d*$/.test(key))
              .map((key) => Number(key.slice("pms_account_id".length) || 0))
          )
        ),
      ]
        .sort((a, b) => a - b)
        .map((index) => ({
          hotelId: index === 0 ? parentHotelId : `${parentHotelId} ${index + 1}`,
          index,
        }));

  return columns.flatMap(({ hotelId, index }) =>
    PAYMENT_FIELD_GROUPS.map((fieldGroup) => ({
      hotelId,
      field: getIndexedFieldName(fieldGroup.base, index),
      label:
        fieldGroup.base === "pms_account_id"
          ? hotelId
          : `${hotelId} ${fieldGroup.shortLabel}`,
      group: fieldGroup.base,
    }))
  );
};

const getPaymentKey = (payment) =>
  [payment.company_id, payment.shop_id, payment.payment_id, payment.payment_name]
    .map((value) => String(value ?? ""))
    .join("|");

const getDisplayPayments = (payments, parentHotelId) => {
  const byPayment = new Map();

  payments.forEach((payment, index) => {
    const key = getPaymentKey(payment);

    if (!byPayment.has(key)) {
      byPayment.set(key, {
        ...payment,
        parent_hotel_id: parentHotelId,
        cross_posting_row_id: `${parentHotelId}-${payment.payment_id}-${index}`,
      });
    }
  });

  return [...byPayment.values()];
};

const PaymentsCrossPosting = () => {
  const { payments, tableLoading, cardTypesLoading, cardTypes } =
    useSelector(companiesSelector);
  const { currentId } = useSelector(currentSelector);
  const { searchQuery } = useSelector(uiSelector);
  const dispatch = useDispatch();

  const hotelPaymentGroups = getHotelPaymentGroups(payments, currentId);
  const cardTypeOptions = cardTypes.map((cardType) => ({
    label: cardType.cardcode,
    value: cardType.cardcode,
  }));

  const getEditableData = (crossPostingColumns) =>
    crossPostingColumns.reduce((result, column) => {
      if (column.group !== "pms_account_id") return result;

      return {
        ...result,
        [column.field]: {
          type: "dropdown",
          placeholder: column.label,
          items: cardTypeOptions,
        },
      };
    }, {});

  const sectionHeadersByHotelId = (hotelId, crossPostingColumns) => [
    {
      id: "pos",
      label: "POS",
      span: 3,
      background: POS_SECTION.background,
      color: POS_SECTION.color,
      dividerAfter: true,
      dividerColor: POS_SECTION.dividerColor,
    },
    {
      id: `hotel-${hotelId}`,
      label: hotelId,
      span: crossPostingColumns.length + 1,
      background: HOTEL_SECTION.background,
      color: HOTEL_SECTION.color,
    },
  ];

  const getHeaderData = (crossPostingColumns) => [
    {
      id: 132,
      label: "Company",
      width: "8%",
      sortable: false,
      section: "pos",
      sectionBackground: POS_SECTION.background,
    },
    {
      id: 232,
      label: "Id",
      width: "6%",
      sortable: true,
      section: "pos",
      sectionBackground: POS_SECTION.background,
    },
    {
      id: 342,
      label: "Name",
      width: "12%",
      sortable: true,
      section: "pos",
      sectionBackground: POS_SECTION.background,
      dividerAfter: true,
      dividerColor: POS_SECTION.dividerColor,
    },
    ...crossPostingColumns.map((column, index) => ({
      id: 400 + index,
      label: column.label,
      width: "7%",
      sortable: true,
      section: "hotel",
      sectionBackground: HOTEL_SECTION.background,
      dividerAfter: (index + 1) % PAYMENT_FIELD_GROUPS.length === 0,
      dividerColor: HOTEL_SECTION.dividerColor,
    })),
    {
      id: 501,
      label: "Action",
      width: "8%",
      sortable: false,
      section: "hotel",
      sectionBackground: HOTEL_SECTION.background,
    },
  ];

  const getRowKeys = (crossPostingColumns) => [
    "company_id",
    "payment_id",
    "payment_name",
    ...crossPostingColumns.map((column) => column.field),
  ];

  const getEditable = (crossPostingColumns) =>
    crossPostingColumns.map((column) => column.field);

  const getSortByData = (crossPostingColumns) => [
    {},
    { label: "payment_id", type: "number" },
    { label: "payment_name", type: "string" },
    ...crossPostingColumns.map((column) => ({
      label: column.field,
      type: column.group === "reservation_id" ? "number" : "string",
    })),
    {},
  ];

  const search = String(searchQuery || "").toLowerCase();
  const filteredGroups = hotelPaymentGroups
    .map((group) => {
      const crossPostingColumns = getCrossPostingColumns(group.payments, group.hotelId);
      const displayPayments = getDisplayPayments(group.payments, group.hotelId);

      return {
        ...group,
        crossPostingColumns,
        payments: displayPayments.filter((payment) => {
          if (!search) return true;

          return (
            String(payment.company_id || "").toLowerCase().includes(search) ||
            String(payment.payment_id || "").toLowerCase().includes(search) ||
            String(payment.payment_name || "").toLowerCase().includes(search) ||
            crossPostingColumns.some((column) =>
              String(payment[column.field] || "").toLowerCase().includes(search)
            )
          );
        }),
      };
    })
    .filter((group) => group.payments.length > 0);

  const updateAction = (x) => {
    dispatch(updatePaymentState(x));
    const { cross_posting_row_id, parent_hotel_id, ...paymentPayload } = x;

    return dispatch(
      updatePayment({ ...paymentPayload, operation_id: "crossposting" })
    );
  };

  if (tableLoading || cardTypesLoading) return <Loading />;

  if (filteredGroups.length === 0 && searchQuery) return <NoSearch />;

  if (filteredGroups.length === 0) return <NoItems />;

  return (
    <TablesWrapper>
      {filteredGroups.map((group) => (
        <HotelTableWrapper key={group.hotelId}>
          <Table
            sortByData={getSortByData(group.crossPostingColumns)}
            headerData={getHeaderData(group.crossPostingColumns)}
            sectionHeaders={sectionHeadersByHotelId(
              group.hotelId,
              group.crossPostingColumns
            )}
            rowKeys={getRowKeys(group.crossPostingColumns)}
            data={group.payments}
            editable={getEditable(group.crossPostingColumns)}
            editableData={getEditableData(group.crossPostingColumns)}
            row_key="cross_posting_row_id"
            updateAction={updateAction}
          />
        </HotelTableWrapper>
      ))}
    </TablesWrapper>
  );
};

export default PaymentsCrossPosting;
